import type {DeployFromRegistryEntryPoint} from '../../components/Registries/workflows/DeployFromRegistry/onDeployFromRegistryClick';
import {recordRumAction} from '../../util/datadog/datadogRum';
import {makeTrackEvent} from './makeTrackEvent';

export type RegistryDeployStep = 'setup' | 'configure' | 'review';

export type TrackRegistryDeployEvent = {
  action:
    | 'deploy_cta_clicked'
    | 'deploy_step_viewed'
    | 'deploy_submitted'
    | 'deploy_started'
    | 'deploy_start_failed'
    | 'deployment_details_opened'
    | 'deployment_clone_clicked'
    | 'deployment_stop_submitted'
    | 'deployment_stopped'
    | 'deployment_stop_failed'
    | 'deployment_endpoint_copied';
  target: 'sandbox';
  location?: DeployFromRegistryEntryPoint;
  intent?: 'deploy' | 'clone';
  step?: RegistryDeployStep;
  entityName?: string;
  projectName?: string;
  registryName?: string;
  collectionName?: string;
  versionIndex?: number;
  deploymentId?: string;
  deploymentName?: string;
  engine?: string;
  computePreset?: string;
  gpuCount?: number;
  lifetimeSeconds?: number;
  errorMessage?: string;
  /** Milliseconds from opening the drawer to the tracked action. */
  durationMs?: number;
};

const trackRegistryDeployInteraction = makeTrackEvent<
  TrackRegistryDeployEvent,
  {
    action: {
      description: 'The deploy workflow action.';
      exampleValues: [
        'deploy_cta_clicked',
        'deploy_step_viewed',
        'deploy_submitted',
        'deploy_started',
        'deploy_start_failed',
        'deployment_details_opened',
        'deployment_clone_clicked',
        'deployment_stop_submitted',
        'deployment_stopped',
        'deployment_stop_failed',
        'deployment_endpoint_copied',
      ];
    };
    target: {
      description: 'Deployment target type.';
      exampleValues: ['sandbox'];
    };
    location: {
      description: 'UI surface that triggered the action.';
      exampleValues: ['collection_preview_menu', 'versions_table'];
    };
    intent: {
      description: 'Whether the drawer is creating or cloning a deployment.';
      exampleValues: ['deploy', 'clone'];
    };
    step: {
      description: 'Drawer step, for step-level actions.';
      exampleValues: ['setup', 'configure', 'review'];
    };
    entityName: {
      description: 'Org entity that owns the registry project.';
      exampleValues: ['<name>'];
    };
    projectName: {
      description: 'Registry project containing the collection.';
      exampleValues: ['wandb-registry-models'];
    };
    registryName: {
      description: 'Registry the deployment deploys from.';
      exampleValues: ['models'];
    };
    collectionName: {
      description: 'Registry collection the deployed version belongs to.';
      exampleValues: ['Qwen3.5-0.8B-GGUF'];
    };
    versionIndex: {
      description: 'Index of the deployed artifact version.';
      exampleValues: [3];
    };
    deploymentId: {
      description: 'GraphQL id of the deployment, when known.';
      exampleValues: ['UmVnaXN0cnlNb2RlbERlcGxveW1lbnQ6NDI='];
    };
    deploymentName: {
      description: 'Name of the deployment.';
      exampleValues: ['qwen-sandbox'];
    };
    engine: {
      description: 'Serving engine selected for the deployment.';
      exampleValues: ['vllm'];
    };
    computePreset: {
      description: 'Named compute preset, or Custom.';
      exampleValues: ['Small', 'Medium', 'Large', 'Custom'];
    };
    gpuCount: {
      description: 'Requested GPU count.';
      exampleValues: [1];
    };
    lifetimeSeconds: {
      description: 'Requested sandbox lifetime.';
      exampleValues: [28800];
    };
    errorMessage: {
      description: 'Server error message when the deploy or stop request failed.';
      exampleValues: ['permission denied'];
    };
    durationMs: {
      description: 'Milliseconds from opening the drawer to the action.';
      exampleValues: [12345];
    };
    _description: 'Tracks the Registry deploy workflow from CTA to submission, and deployment lifecycle actions.';
    _location: 'Registry collection pages, version tables, and deploy drawers';
    _motivation: 'Measure funnel drop-off and failure rate of Registry deployments.';
  }
>('Registry Deploy Interaction');

export function trackRegistryDeployEvent(
  eventData: TrackRegistryDeployEvent
): void {
  trackRegistryDeployInteraction(eventData);
  recordRumAction(`RegistryDeploy.${eventData.action}`, eventData);
}
